import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Clock, CheckCircle2, ListTodo, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const AVATAR_COLORS = [
  "bg-pink-500", "bg-violet-500", "bg-emerald-500", "bg-orange-500",
  "bg-rose-500", "bg-sky-500", "bg-amber-500", "bg-indigo-500",
];

function minutesToHuman(m) {
  if (!m || m <= 0) return "0h 0m";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

function statusColor(status) {
  return {
    todo: "bg-neutral-100 text-neutral-700",
    in_progress: "bg-sky-100 text-sky-800",
    review: "bg-amber-100 text-amber-800",
    done: "bg-emerald-100 text-emerald-800",
    blocked: "bg-rose-100 text-rose-800",
  }[status] || "bg-neutral-100 text-neutral-700";
}

export default function MemberDrillDown({ userId, avatarIdx = 0, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setError(null);
    api.get(`/team-productivity/${userId}/detail?weeks=8`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load detail"))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const u = data?.user;
  const initials = (u?.name || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
  const weeklyTarget = (u?.weekly_capacity_hours || 40);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="member-drilldown-modal">
        <DialogHeader className="px-6 py-5 border-b border-neutral-200">
          <DialogTitle className="sr-only">Member productivity detail</DialogTitle>
          <DialogDescription className="sr-only">Weekly hours, top projects and recent tasks for this team member.</DialogDescription>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 ${AVATAR_COLORS[avatarIdx % AVATAR_COLORS.length]} text-white rounded-full grid place-items-center text-base font-semibold shrink-0`}>
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">Member detail · last 8 weeks</div>
              <div className="font-display text-xl font-bold truncate">{u?.name || "—"}</div>
              <div className="text-xs text-neutral-500 truncate">{u?.email || ""} · {u?.role}</div>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {loading && <div className="text-sm text-neutral-500 py-8 text-center">Loading…</div>}
          {error && <div className="text-sm text-rose-600 py-8 text-center">{error}</div>}

          {!loading && !error && data && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-3 gap-3 mb-6" data-testid="drilldown-totals">
                <div className="border border-neutral-200 p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                    <Clock size={12} /> Hours · 8w
                  </div>
                  <div className="font-display text-xl font-bold">{minutesToHuman(data.totals.hours_minutes)}</div>
                </div>
                <div className="border border-neutral-200 p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                    <CheckCircle2 size={12} /> Tasks done
                  </div>
                  <div className="font-display text-xl font-bold">{data.totals.tasks_done}</div>
                </div>
                <div className="border border-neutral-200 p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                    <ListTodo size={12} /> Open tasks
                  </div>
                  <div className="font-display text-xl font-bold">{data.totals.tasks_open}</div>
                </div>
              </div>

              {/* Weekly bar chart */}
              <div className="mb-6" data-testid="drilldown-weekly-chart">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Weekly hours</div>
                <div className="border border-neutral-200 p-4 bg-white">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.weekly} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`${v}h`, "Hours"]} />
                      <ReferenceLine y={weeklyTarget} stroke="#EF4444" strokeDasharray="4 4" label={{ value: `Target ${weeklyTarget}h`, fontSize: 10, fill: "#EF4444", position: "right" }} />
                      <Bar dataKey="hours" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top projects */}
              <div className="mb-6" data-testid="drilldown-top-projects">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Top projects · last 8 weeks</div>
                <div className="border border-neutral-200 bg-white">
                  {data.top_projects.length === 0 ? (
                    <div className="p-6 text-center text-sm text-neutral-400">No project hours logged in this window.</div>
                  ) : data.top_projects.map(p => {
                    const max = data.top_projects[0].hours_minutes || 1;
                    const pct = Math.round((p.hours_minutes / max) * 100);
                    return (
                      <Link key={p.id} to={`/projects/${p.id}`} onClick={onClose} className="block px-5 py-3 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors" data-testid={`drilldown-project-${p.id}`}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-1.5 h-5" style={{ background: p.color || "#0A0A0A" }} />
                            <span className="text-sm font-medium truncate">{p.name}</span>
                          </div>
                          <span className="text-sm font-display font-bold tabular-nums">{p.hours}h</span>
                        </div>
                        <div className="h-1.5 bg-neutral-100 overflow-hidden">
                          <div className="h-full bg-violet-500" style={{ width: `${pct}%` }} />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Recent tasks */}
              <div data-testid="drilldown-recent-tasks">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Recent tasks</div>
                <div className="border border-neutral-200 bg-white">
                  {data.recent_tasks.length === 0 ? (
                    <div className="p-6 text-center text-sm text-neutral-400">No assigned tasks.</div>
                  ) : data.recent_tasks.map(t => (
                    <Link key={t.id} to={`/projects/${t.project_id}`} onClick={onClose} className="px-5 py-3 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 flex items-center justify-between gap-3 transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{t.title}</div>
                        {t.due_date && <div className="text-[10px] text-neutral-500 mt-0.5">Due {t.due_date}</div>}
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${statusColor(t.status)}`}>
                        {(t.status || "").replace("_", " ")}
                      </span>
                      <ExternalLink size={12} className="text-neutral-300" />
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
